import { useEffect, useState } from "react";
import { articleList, categoryOptions, type ArticleItem } from "@/mocks/guaData";

const categoryIcon: Record<string, string> = {
  综艺: "ri-remote-control-fill",
  明星: "ri-star-smile-fill",
  网红: "ri-user-heart-fill",
  影视: "ri-movie-2-fill",
  音乐: "ri-music-2-fill",
};

function ArticleCard({ item }: { item: ArticleItem }) {
  return (
    <article className="group flex gap-4 md:gap-5 p-3 md:p-4 rounded-xl bg-background-50 border border-background-200 hover:border-primary-200 transition-colors">
      <div className="w-24 sm:w-32 md:w-40 lg:w-44 shrink-0 overflow-hidden rounded-lg bg-background-200 h-24 sm:h-28 md:h-28">
        <img
          src={item.cover}
          alt={`${item.title}文章配图`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="min-w-0 flex-1 flex flex-col py-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-primary-100 text-primary-700">
            {item.tag}
          </span>
          <span className="text-xs text-foreground-500 flex items-center gap-1">
            <i className={`${categoryIcon[item.category] ?? "ri-article-line"} text-primary-500`}></i>
            {item.category}
          </span>
        </div>
        <h3 className="font-medium text-foreground-950 text-[15px] md:text-base mt-1.5 leading-snug group-hover:text-primary-700 line-clamp-2 transition-colors">
          {item.title}
        </h3>
        <p className="hidden md:block text-foreground-500 text-sm mt-1.5 leading-relaxed line-clamp-1">
          {item.summary}
        </p>
        <div className="mt-auto pt-2 flex items-center gap-3 text-xs text-foreground-400 flex-wrap">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-eye-line"></i> {item.reads}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-chat-3-line"></i> {item.comments}
          </span>
          <span className="ml-auto hidden sm:inline-flex items-center gap-1 whitespace-nowrap">
            {item.time}
          </span>
        </div>
      </div>
    </article>
  );
}

export default function ContentFlow() {
  const [active, setActive] = useState("全部");
  const [kw, setKw] = useState("");
  const [list, setList] = useState<ArticleItem[]>(articleList);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail || "";
      setKw(detail);
      setActive("全部");
      document.getElementById("content")?.scrollIntoView({ behavior: "smooth" });
    };
    window.addEventListener("gua-search", handler);
    return () => window.removeEventListener("gua-search", handler);
  }, []);

  useEffect(() => {
    let filtered = articleList;
    if (active !== "全部") filtered = filtered.filter((a) => a.category === active);
    if (kw.trim()) {
      const key = kw.trim().toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.title.toLowerCase().includes(key) ||
          a.summary.toLowerCase().includes(key) ||
          a.tag.toLowerCase().includes(key)
      );
    }
    setList(filtered);
  }, [active, kw]);

  return (
    <section id="content" className="px-4 md:px-8 py-12 md:py-16 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <div>
            <span className="inline-flex items-center gap-1.5 text-primary-600 font-medium text-sm">
              <i className="ri-article-fill"></i> 精选内容 · 每日更新
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-2">
              最近在吃什么瓜？
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
              <input
                type="search"
                value={kw}
                onChange={(e) => setKw(e.target.value)}
                placeholder="筛选我关心的内容"
                className="h-10 rounded-lg bg-background-100 border border-background-200 pl-9 pr-3 text-sm outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 w-full sm:w-56 placeholder:text-foreground-400"
              />
            </div>
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-lg border border-primary-200 bg-primary-50 text-primary-700 text-sm font-medium hover:bg-primary-100 whitespace-nowrap"
            >
              <i className="ri-add-line"></i> 提供瓜线索
            </a>
          </div>
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500 mr-1 whitespace-nowrap">热门分类：</span>
          {categoryOptions.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActive(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                active === cat
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200"
              }`}
            >
              {cat === "全部" ? "全部内容" : cat}
            </button>
          ))}
        </div>

        {list.length > 0 ? (
          <div className="mt-6 grid lg:grid-cols-2 gap-3 md:gap-4">
            {list.map((item) => (
              <ArticleCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="mt-10 py-16 text-center rounded-2xl bg-background-100 border border-background-200">
            <i className="ri-inbox-2-line text-4xl text-foreground-300"></i>
            <p className="mt-3 text-foreground-600 font-medium">暂时没有找到相关内容</p>
            <p className="text-foreground-400 text-sm mt-1">换个分类或关键词再试试吧</p>
            <button
              type="button"
              onClick={() => {
                setKw("");
                setActive("全部");
              }}
              className="mt-5 px-5 h-10 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600"
            >
              清除筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}